import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_CLS = { active: 'bg-emerald-100 text-emerald-800', draft: 'bg-sky-100 text-sky-800', paused: 'bg-amber-100 text-amber-800', cancelled: 'bg-slate-200 text-slate-600' };

export function PlanList({ onOpen, onNew, refreshKey }) {
  const { api } = useAuth();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get('/maintenance-plans', { params: { status } }).then(r => setRows(r.data)).catch(() => toast.error('Could not load plans')), [api, status]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!rows) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;
  const shown = rows.filter(p => !q || `${p.client_name} ${p.address}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="plan-list" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <button data-testid="mplan-new-btn" onClick={onNew} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-[#D35400] text-white hover:bg-[#A04000]"><Plus size={14} />New plan</button>
        {['', 'active', 'draft', 'paused', 'cancelled'].map(s => <button key={s || 'all'} data-testid={`mplan-filter-${s || 'all'}`} onClick={() => setStatus(s)} className={`px-3 py-1 text-xs rounded-full border ${status === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:bg-slate-50'}`}>{s ? s[0].toUpperCase() + s.slice(1) : 'All open'}</button>)}
        <div className="relative ml-auto"><Search size={13} className="absolute left-2 top-2 text-slate-400" /><input data-testid="mplan-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Client or address" className="pl-7 pr-2 py-1.5 text-xs border border-slate-300 rounded w-56" /></div>
      </div>
      <div className="border border-[#E2E2DF] rounded-md bg-white divide-y divide-[#F0EFEC]">
        {shown.map(p => (
          <button key={p._id} data-testid={`mplan-row-${p._id}`} onClick={() => onOpen(p._id)} className="w-full text-left p-4 hover:bg-slate-50 transition-colors">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-slate-900">{p.client_name || 'Client'}</span>
              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${STATUS_CLS[p.status]}`}>{p.status}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">from {p.source}</span>
              <span className="ml-auto text-xs text-slate-500">{p.address}</span>
            </div>
            <p className="text-xs text-slate-600 mt-1">{p.services.filter(s => s.active).map(s => `${s.label} every ${s.every_months % 12 === 0 ? `${s.every_months / 12}y` : `${s.every_months}m`}${s.next_due ? ` (next ${s.next_due})` : ''}`).join(' · ')}</p>
          </button>
        ))}
        {shown.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">No plans yet. Accepted maintenance quotes and Reconnect replies create them automatically, or add one with New plan.</p>}
      </div>
    </div>
  );
}

export function ReminderSettings() {
  const { api } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/maintenance-plans/defaults').then(r => setS(r.data.settings)); }, [api]);
  if (!s) return <Loader2 className="animate-spin text-slate-400" />;
  const save = async (patch) => {
    const prev = s; const next = { ...s, ...patch };
    setS(next); setSaving(true);
    try { const r = await api.put('/maintenance-plans/settings', { ...next, public_base_url: window.location.origin }); setS(r.data); toast.success('Reminder settings saved'); }
    catch (e) { setS(prev); toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="mplan-settings" className="border border-[#E2E2DF] rounded-md bg-white p-5 space-y-4 max-w-xl">
      <label className="flex items-center gap-3">
        <input data-testid="mplan-reminders-toggle" type="checkbox" disabled={saving} checked={s.reminders_enabled} onChange={e => save({ reminders_enabled: e.target.checked })} />
        <span className="text-sm"><b>Email clients a reminder</b> before each visit is due (sent 8am daily, once per due date, never to opted-out clients)</span>
      </label>
      <label className="flex items-center gap-2 text-sm">Send it
        <input data-testid="mplan-reminder-days" type="number" min="3" max="60" value={s.reminder_days} onChange={e => setS({ ...s, reminder_days: Number(e.target.value) })} onBlur={() => save({})} className="w-16 px-2 py-1 border border-slate-300 rounded" />
        days before the due date</label>
      <p className="text-xs text-slate-500">Clients click a link to confirm, skip this time or ask for a call — replies show on the Due soon list and email you an alert. Tip: open a plan and use "Send test reminder" first.</p>
    </div>
  );
}
